import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import LoadingIndicator from "../../components/common/loadingIndicator/LoadingIndicator";
import Toast from "../../components/common/toast/Toast";
import { BasicCoupon, ErrorValidation } from "../../types/types";
import DeleteButton from "../../components/common/buttons/DeleteButton";
import { createCoupons, deleteCoupon, getCoupons } from "../../service/couponService";
import { deleteBaggage } from "../../service/baggageService";
const Coupon: React.FC = () => {
  const [showToast, setShowToast] = useState<boolean>(false);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<"success" | "error" | "warning">(
    "success"
  );
  const [couponList, setCouponList] = useState<BasicCoupon[]>([]);
  const [coupon, setCoupon] = useState<BasicCoupon>({
    id: 0,
    couponCode: "",
    percentage: "",
  });

  const location = useLocation();

  useEffect(() => {
    fetchCoupons();
    if (location.state) {
      setCoupon({ ...location.state, value: "" });
    }
  }, [location]);

  const fetchCoupons = async () => {
    setIsDataLoading(true);
    const coupons = await getCoupons();
    setCouponList(coupons || []);
    setIsDataLoading(false);
  };

  const validateForm = (): ErrorValidation => {
    const textRegex = /^[A-Za-z0-9\s]+$/; // Allows alphabetic characters, numbers and spaces only
    const { couponCode, percentage } = coupon;

    for (const existingCoupon of couponList) {
      if (existingCoupon.couponCode === couponCode) {
        return {
          message: "Coupon value already added for this code.",
          validate: false,
        };
      }
    }

    if (couponCode.trim() === "" || percentage.trim() === "") {
      return {
        message: "All fields are required.",
        validate: false,
      };
    }
    if (!textRegex.test(couponCode.trim())) {
      return {
        message: "Coupon code should contain only letters and numbers.",
        validate: false,
      };
    }

    if (percentage.trim() === "") {
      return {
        message: "Baggage value is required.",
        validate: false,
      };
    }

    return {
      message: "",
      validate: true,
    };
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
  
    setCoupon((prev) => ({
      ...prev,
      [name]: name === "couponCode" ? value.toUpperCase() : value, // ✅ Convert only `couponCode` to uppercase
    }));
  };

  const handleUpdate = async () => {
    const validateInfo = validateForm();
    if (validateInfo.validate === false) {
      setToastMessage(validateInfo.message);
      setShowToast(true);
      setToastType("error");
      return;
    }

    setIsDataLoading(true);

    const response = await createCoupons(coupon);

    setIsDataLoading(false);

    setCouponList((prev) => [
      ...prev,
      { ...coupon }, // Generate a unique ID
    ]);

    // Reset the form
    setCoupon({
      id: 0,
      couponCode: "",
      percentage: "",
    });

    setShowToast(true);
    setToastMessage("Coupon added successfully!");
    setToastType("success");
  };

  const handleDelete = async (id: number) => {
    setIsDataLoading(true);
    const response = await deleteCoupon(id);
    setCouponList((prev) => prev.filter((coupon) => coupon.id !== id));
    setIsDataLoading(false);
  };

  return (
    <div className="container mx-auto p-6">
      {showToast && (
        <Toast
          type={toastType}
          onClose={() => setShowToast(false)}
          message={toastMessage}
        />
      )}
      {isDataLoading && <LoadingIndicator></LoadingIndicator>}
      <h2 className="text-3xl font-bold mb-6">Coupon Settings</h2>

      <div className="">
        <div className="">
          <div className="overflow-x-auto bg-white shadow-md rounded-lg overflow-hidden mb-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">
                    Coupon Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Percentage
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      maxLength={10}
                      type="text"
                      name="couponCode"
                      value={coupon.couponCode}
                      className="border border-gray-300 rounded p-2"
                      onChange={handleValueChange}
                      onInput={(e) => {
                        e.currentTarget.value = e.currentTarget.value.toUpperCase(); 
                      }}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap flex items-center gap-1">
                    <input
                      type="number"
                      name="percentage"
                      value={coupon.percentage}
                      className="border border-gray-300 rounded p-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      onChange={handleValueChange}
                    />
                    <div className="font-bold text-blue-300">%</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-right">
            <button
              className={`bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-900 
              `}
              onClick={handleUpdate}
            >
              Add
            </button>
          </div>
          <div className="overflow-x-auto mt-6 bg-white shadow-md rounded-lg overflow-hidden mb-6">
            {!couponList || couponList.length === 0 ? (
              <div className="p-2 flex justify-center text-sm text-gray-400">
                No coupon details found.
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coupon Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Percentage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Settings
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {couponList.map((coupon) => (
                    <tr key={coupon.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {coupon.couponCode}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {coupon.percentage} {<span>%</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <DeleteButton
                          onConfirm={() => {
                            handleDelete(coupon.id);
                          }}
                        ></DeleteButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Coupon;
